import './styles.css';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { RoutalPalette } from '../../new_components';

import _ from 'lodash';
import { GoogleMapsProvider, useGoogleMapsLibraries } from './context/GoogleMapsProvider';
import { GeoFenceDomain, GeoFenceDomainProps } from './geo-fence/GeoFenceDomain';
import { MarkerDomain, MarkerDomainProps } from './marker/MarkerDomain';

const MAP_MIN_ZOOM = 3;
const MAP_MAX_ZOOM = 19;

export type LatLng = { lat: number; lng: number };

const defaultMapProps = {
  height: 500,
  center: {
    lat: 41.3952165,
    lng: 2.1258438,
  },
  zoomLevel: 11,
};

type GoogleMapGeoFencesComponentProps = {
  center?: LatLng;
  height?: number | string;
  zoomLevel?: number;
  language?: string;
  isDrawing: boolean;
  markers?: MarkerDomainProps[];
  geoFences?: GeoFenceDomainProps[];
  loadingComponent?: React.ReactElement;
  disableDoubleClickZoom?: boolean;
  onGeoFenceDrawn?: (...args: any[]) => any;
};

const GoogleMapGeoFencesComponent = ({
  center,
  height,
  zoomLevel,
  isDrawing,
  markers,
  geoFences,
  loadingComponent,
  disableDoubleClickZoom,
  onGeoFenceDrawn,
}: GoogleMapGeoFencesComponentProps) => {
  const { googleMapsLoaded } = useGoogleMapsLibraries();

  const mapRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const [mapZoom, setMapZoom] = useState<number>(zoomLevel ?? defaultMapProps.zoomLevel);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(center ?? defaultMapProps.center);

  const [mapMarkers, setMapMarkers] = useState<Record<string, MarkerDomain>>({});
  const [mapGeoFences, setMapGeoFences] = useState<Record<string, GeoFenceDomain>>({});
  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | undefined>();

  const [isFullyLoaded, setIsFullyLoaded] = useState<boolean>(false);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);

  useEffect(() => {
    if (mapRef.current && googleMapsLoaded && !isFullyLoaded) {
      const newMap = new google.maps.Map(mapRef.current, {
        //   key: apiKey,
        //   libraries: [`drawing`],
        mapId: `8750043978b480cc`,
        disableDefaultUI: true,
        center: center ?? defaultMapProps.center,
        zoom: zoomLevel ?? defaultMapProps.zoomLevel,
        scrollwheel: true,
        disableDoubleClickZoom: disableDoubleClickZoom ?? false,
        maxZoom: MAP_MAX_ZOOM,
        minZoom: MAP_MIN_ZOOM,
        zoomControl: true,
        // zoomControlOptions: {
        //   position: window.google ? google.maps.ControlPosition.RIGHT_BOTTOM : 6.0,
        // },
        scaleControl: false,
        rotateControl: false,
        fullscreenControl: false,
        streetViewControl: false,
      });

      const dM = new google.maps.drawing.DrawingManager();

      dM.setOptions({
        drawingMode: google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google ? google.maps.ControlPosition.RIGHT_BOTTOM : 6.0,
          drawingModes: window.google ? [] : [],
        },
        polygonOptions: {
          strokeColor: RoutalPalette.primary40,
          strokeWeight: 4,
          strokeOpacity: 0.4,
          fillColor: RoutalPalette.primary40,
          fillOpacity: 0.2,
        },
      });

      google.maps.event.addListener(dM, `polygoncomplete`, (polygon: any) => {
        if (onGeoFenceDrawn) {
          const path = polygon.getPath();

          const coordinates: { lat?: number; lng?: number }[] = [];
          path.forEach((point: google.maps.LatLng) => coordinates.push({ lat: point.lat(), lng: point.lng() }));

          onGeoFenceDrawn([...coordinates, coordinates[0]]);

          // Remove the drawn polygon
          polygon.setMap(null);

          // Disables the drawing manager tool
          dM?.setMap(null);
        }
      });

      setDrawingManager(dM);

      setMap(newMap);
      setIsFullyLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMapsLoaded]);

  useEffect(() => {
    if (zoomLevel && zoomLevel !== mapZoom) {
      setMapZoom(zoomLevel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomLevel]);

  useEffect(() => {
    if (center && center.lat !== mapCenter.lat && center.lng !== mapCenter.lng) {
      setMapCenter(center);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center]);

  useEffect(() => {
    if (map) {
      drawingManager?.setMap(isDrawing ? map : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing]);

  const drawMarkers = useCallback(() => {
    const newMapMarkersMap = (markers ?? []).reduce((amount, markerProps) => {
      const marker = MarkerDomain.create(markerProps);
      marker.googleMarker!.map = map!;
      return {
        ...amount,
        [marker.id]: marker,
      };
    }, {});

    if (!_.isEqual(mapMarkers, newMapMarkersMap)) {
      setMapMarkers(newMapMarkersMap);
    }
  }, [map, mapMarkers, markers]);

  useEffect(() => {
    if (isFullyLoaded) {
      drawMarkers();
      drawGeoFences();

      if (isFirstTime && (geoFences ?? []).length > 0) {
        // Fit bounds to geo fences points
        const bounds = new google.maps.LatLngBounds();

        const geoFencesPoints = geoFences!.reduce((points, geoFence) => {
          return points.concat(...geoFence.paths.map((polygon) => polygon));
        }, [] as Array<{ lat: number; lng: number }>);

        geoFencesPoints.forEach((point: { lat: number; lng: number }) => {
          bounds.extend(new google.maps.LatLng(point.lat, point.lng));
        });

        map?.fitBounds(bounds);
      }
      if (isFirstTime) {
        setIsFirstTime(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyLoaded, geoFences]);

  const drawGeoFences = useCallback(() => {
    let visibility = geoFences!.reduce(
      (
        amount: {
          toAdd: GeoFenceDomain[];
          toAddMap: Record<string, GeoFenceDomain>;
          toRemove: GeoFenceDomain[];
          toRemoveMap: Record<string, GeoFenceDomain>;
        },
        geoFenceProps: GeoFenceDomainProps
      ) => {
        const toAdd = [...amount.toAdd];
        let toAddMap = { ...amount.toAddMap };
        const toRemove = [...amount.toRemove];
        let toRemoveMap = { ...amount.toRemoveMap };

        const geoFenceProp = GeoFenceDomain.create(geoFenceProps);
        const mapGeoFence = mapGeoFences[geoFenceProp.id as string];

        if (!mapGeoFence) {
          toAdd.push(geoFenceProp);
          toAddMap = {
            ...toAddMap,
            [geoFenceProp.id]: geoFenceProp,
          };
        } else if (!geoFenceProp.isEqual(mapGeoFence)) {
          toRemove.push(mapGeoFence);
          toRemoveMap = {
            ...toRemoveMap,
            [mapGeoFence.id]: mapGeoFence,
          };

          toAdd.push(geoFenceProp);
          toAddMap = {
            ...toAddMap,
            [geoFenceProp.id]: geoFenceProp,
          };
        }

        return {
          toAdd,
          toAddMap,
          toRemove,
          toRemoveMap,
        };
      },
      {
        toAdd: [],
        toAddMap: {},
        toRemove: [],
        toRemoveMap: {},
      }
    );

    visibility = Object.values(mapGeoFences).reduce((amount, mapGeoFence) => {
      const toAdd = [...amount.toAdd];
      const toAddMap = { ...amount.toAddMap };
      const toRemove = [...amount.toRemove];
      let toRemoveMap: Record<string, GeoFenceDomain> = { ...amount.toRemoveMap };

      const markerProp = geoFences!.find((geoFence) => geoFence.id === mapGeoFence.id);

      // MapMarker exists in mapGeoFences but not in the geoFence array property, remove it.
      if (!markerProp) {
        toRemove.push(mapGeoFence);
        toRemoveMap = {
          ...toRemoveMap,
          [mapGeoFence.id]: mapGeoFence,
        };
      }

      return {
        toAdd,
        toAddMap,
        toRemove,
        toRemoveMap,
      };
    }, visibility);

    if (visibility.toAdd.length === 0 && visibility.toRemove.length === 0) {
      return;
    }

    const mapGeoFencesArray = Object.values(mapGeoFences);

    // Cleans the mapMarkers that should be removed.
    let newMapGeoFences = mapGeoFencesArray.filter((geoFence) => visibility.toRemoveMap[geoFence.id] === undefined);

    if (visibility.toRemove.length > 0) {
      visibility.toRemove.forEach((geoFence) => {
        geoFence.removeMap();
      });
    }

    if (visibility.toAdd.length > 0) {
      // Add markers to the map
      visibility.toAdd.forEach((geoFence) => {
        geoFence.setMap(map!);
      });

      newMapGeoFences = [...newMapGeoFences, ...visibility.toAdd];
    }

    const newMapGeoFencesMap = newMapGeoFences.reduce(
      (amount, geoFence) => ({ ...amount, [geoFence.id]: geoFence }),
      {}
    );

    if (!_.isEqual(mapGeoFences, newMapGeoFencesMap)) {
      setMapGeoFences(newMapGeoFencesMap);
    }
  }, [geoFences, mapGeoFences, map]);

  return (
    <div
      style={{
        position: `relative`,
        width: `100%`,
        height: height ?? defaultMapProps.height,
      }}
    >
      {loadingComponent && !googleMapsLoaded ? (
        <div
          style={{
            display: `flex`,
            position: `absolute`,
            width: `100%`,
            height: `100%`,
            flexDirection: `column`,
            alignItems: `center`,
            justifyContent: `center`,
            zIndex: 100,
            backgroundColor: `#fcf2e35e`,
          }}
        >
          {loadingComponent}
        </div>
      ) : null}

      <div
        id="map"
        ref={mapRef}
        style={{
          height: `100%`,
          width: `100%`,
        }}
      />
    </div>
  );
};

const MemoizedGoogleMapGeoFences = memo(GoogleMapGeoFencesComponent, areEqual);

function areEqual(prevProps: GoogleMapGeoFencesComponentProps, nextProps: GoogleMapGeoFencesComponentProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  if (!_.isEqual(prevProps.center, nextProps.center)) {
    // console.info(`------------- GoogleMap areEqual -> center has changed`);
    return false;
  }
  if (!_.isEqual(prevProps.height, nextProps.height)) {
    // console.info(`------------- GoogleMap areEqual -> height has changed`);
    return false;
  }
  if (!_.isEqual(prevProps.zoomLevel, nextProps.zoomLevel)) {
    // console.info(`------------- GoogleMap areEqual -> zoomLevel has changed`);
    return false;
  }
  if (!_.isEqual(prevProps.language, nextProps.language)) {
    // console.info(`------------- GoogleMap areEqual -> language has changed`);
    return false;
  }
  if (!_.isEqual(prevProps.isDrawing, nextProps.isDrawing)) {
    // console.info(`------------- GoogleMap areEqual -> isDrawing has changed`);
    return false;
  }
  if (!_.isEqual(prevProps.markers, nextProps.markers)) {
    // console.info(`------------- GoogleMap areEqual -> markers has changed`);
    return false;
  }
  if (
    !_.isEqual(
      prevProps.geoFences?.map((geoFence) => _.pick(geoFence, [`id`, `color`, `paths`, `eventsUpdateHash`])),
      nextProps.geoFences?.map((geoFence) => _.pick(geoFence, [`id`, `color`, `paths`, `eventsUpdateHash`]))
    )
  ) {
    return false;
  }
  if (!_.isEqual(prevProps.disableDoubleClickZoom, nextProps.disableDoubleClickZoom)) {
    // console.info(`------------- GoogleMap areEqual -> disableDoubleClickZoom has changed`);
    return false;
  }

  return true;
}

export interface GoogleMapGeoFencesProps extends GoogleMapGeoFencesComponentProps {
  apiKey: string;
  language?: string;
}

export const GoogleMapGeoFences = ({ apiKey, language = `es`, ...props }: GoogleMapGeoFencesProps) => {
  return (
    <GoogleMapsProvider apiKey={apiKey} language={language}>
      <MemoizedGoogleMapGeoFences {...props} />
    </GoogleMapsProvider>
  );
};
